
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    MapPin,
    RefreshCw,
    ImageOff,
} from "lucide-react";

const API = "https://dubaibudgetivehotels.com/api";
const HOTEL_IMG = "https://uaediscountedhotels.com/uploads/hotels";

const CITY_NAMES = new Set([
    "dubai",
    "abu dhabi",
    "sharjah",
    "ajman",
    "fujairah",
    "ras al khaimah",
    "umm al quwain",
    "al ain",
    "uae",
    "united arab emirates",
]);

const cleanText = (value) =>
    String(value ?? "")
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .trim();

const normalize = (value) => cleanText(value).toLowerCase();

function getList(response) {
    const data = response?.data;

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.hotels)) return data.hotels;
    if (Array.isArray(data?.data?.data)) return data.data.data;
    if (Array.isArray(data?.data?.hotels)) {
        return data.data.hotels;
    }

    return [];
}

function getAreaName(hotel) {
    const candidates = [
        hotel.area?.name,
        hotel.neighborhood?.name,
        hotel.district?.name,
        hotel.area_name,
        hotel.neighborhood,
        hotel.district,
        hotel.locality,
        hotel.location,
    ];

    for (const candidate of candidates) {
        if (typeof candidate !== "string") continue;

        const parts = cleanText(candidate)
            .split(",")
            .map(cleanText)
            .filter(Boolean);

        const area = parts.find(
            (part) => !CITY_NAMES.has(normalize(part))
        );

        if (area) return area;
    }

    return "";
}

function getImage(value) {
    if (!value) return "";

    if (typeof value === "object") {
        return getImage(
            value.url ||
            value.image_url ||
            value.path ||
            value.image
        );
    }

    const image = cleanText(value);
    if (!image) return "";

    if (/^https?:\/\//i.test(image)) return image;

    if (image.startsWith("//")) {
        return `${window.location.protocol}${image}`;
    }

    if (image.startsWith("/")) {
        return `${window.location.origin}${image}`;
    }

    return `${HOTEL_IMG}/${image}`;
}

function getHotelImage(hotel) {
    return getImage(
        hotel.image ||
        hotel.featured_image ||
        hotel.thumbnail ||
        hotel.cover_image ||
        hotel.images?.[0] ||
        hotel.gallery?.[0]
    );
}

function getHotelPrice(hotel) {
    const candidates = [
        hotel.price_per_night,
        hotel.starting_price,
        hotel.min_price,
        hotel.price,
        hotel.room_price,
    ];

    for (const value of candidates) {
        if (value === null || value === undefined || value === "") {
            continue;
        }

        const price = Number(
            String(value).replace(/[^0-9.]/g, "")
        );

        if (Number.isFinite(price) && price > 0) {
            return price;
        }
    }

    return null;
}

const AREA_DESCRIPTIONS = {
    "downtown dubai": "Burj Khalifa & Dubai Mall",
    "dubai marina": "Waterfront Views",
    deira: "Traditional Dubai",
    "business bay": "Modern & Central",
    "jumeirah beach": "Beachside Stays",
    jumeirah: "Beachside Stays",
    "palm jumeirah": "Island & Beach Resorts",
    "al barsha": "Shopping & City Stays",
    burdubai: "Culture & Heritage",
    "bur dubai": "Culture & Heritage",
    "jumeirah lake towers": "Lakeside City Living",
    jlt: "Lakeside City Living",
};

function AreaCard({ area, onClick }) {
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        setImageFailed(false);
    }, [area.image]);

    const description =
        AREA_DESCRIPTIONS[normalize(area.name)] ||
        "Discover Dubai";

    return (
        <article
            className="
        group relative isolate
        h-[255px] w-full overflow-hidden
        rounded-[11px] bg-[#064E3B]
        shadow-[0_3px_12px_rgba(0,0,0,0.07)]
        transition-all duration-300
        hover:-translate-y-1
        hover:shadow-[0_12px_28px_rgba(0,55,35,0.17)]
        sm:h-[275px]
        lg:h-[300px]
      "
        >
            {/* DESTINATION IMAGE */}
            {area.image && !imageFailed ? (
                <img
                    src={area.image}
                    alt={`${area.name}, Dubai`}
                    loading="lazy"
                    onError={() => setImageFailed(true)}
                    className="
            absolute inset-0 h-full w-full object-cover
            transition-transform duration-700
            group-hover:scale-110
          "
                />
            ) : (
                <div
                    className="
            absolute inset-0 flex items-center
            justify-center bg-gradient-to-br
            from-[#299A67] to-[#064E3B]
          "
                >
                    <MapPin size={40} className="text-white/40" />
                </div>
            )}

            {/* DARK BOTTOM GRADIENT */}
            <div
                className="
          pointer-events-none absolute inset-0
          bg-gradient-to-t
          from-[#031F19]/95
          via-[#032F22]/20
          to-transparent
        "
            />

            {/* FULL CARD CLICK */}
            <button
                type="button"
                onClick={() => onClick(area)}
                aria-label={`Explore hotels in ${area.name}`}
                className="
          absolute inset-0 z-10 cursor-pointer
          rounded-[11px]
          focus-visible:outline-2
          focus-visible:outline-offset-2
          focus-visible:outline-[#168B43]
        "
            />

            {/* BOTTOM CONTENT */}
            <div
                className="
          pointer-events-none absolute inset-x-0
          bottom-0 z-20 px-3.5 pb-3.5 pt-10
          sm:px-4 sm:pb-4
        "
            >
                <h3
                    className="
            text-[15px] font-extrabold
            leading-[1.2] tracking-[-0.025em]
            text-white sm:text-[16px]
          "
                >
                    {area.name}
                </h3>

                <p
                    className="
            mt-1 text-[10px] font-medium
            leading-4 text-white/85
          "
                >
                    {description}
                </p>

                <div className="mt-2 flex items-end justify-between gap-2">
                    <div className="min-w-0">
                        {area.minPrice !== null ? (
                            <p className="text-[11px] font-bold text-white">
                                From AED{" "}
                                <span className="text-[14px]">
                                    {Math.round(area.minPrice).toLocaleString()}
                                </span>
                            </p>
                        ) : (
                            <p className="text-[11px] font-semibold text-white">
                                {area.hotelCount}{" "}
                                {area.hotelCount === 1 ? "Hotel" : "Hotels"}
                            </p>
                        )}
                    </div>

                    {/* GREEN ROUND ARROW */}
                    <span
                        className="
              flex h-8 w-8 shrink-0
              items-center justify-center
              rounded-full bg-[#168B43]
              text-white shadow-md
              transition-all duration-300
              group-hover:bg-[#20A451]
              group-hover:scale-110
            "
                    >
                        <ArrowRight size={16} strokeWidth={2.5} />
                    </span>
                </div>
            </div>
        </article>
    );
}

export default function PopularAreas() {
    const navigate = useNavigate();

    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [retryKey, setRetryKey] = useState(0);

    useEffect(() => {
        const controller = new AbortController();

        async function loadHotels() {
            setLoading(true);
            setError("");

            try {
                const response = await axios.get(`${API}/hotels`, {
                    signal: controller.signal,
                });

                if (controller.signal.aborted) return;

                setHotels(getList(response));
            } catch (err) {
                if (controller.signal.aborted) return;

                console.error("Popular areas API error:", err);
                setError("Unable to load popular areas right now.");
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }

        loadHotels();

        return () => controller.abort();
    }, [retryKey]);

    const areas = useMemo(() => {
        const groups = new Map();

        hotels.forEach((hotel) => {
            const name = getAreaName(hotel);
            if (!name) return;

            const key = normalize(name);
            const image = getHotelImage(hotel);
            const price = getHotelPrice(hotel);

            if (!groups.has(key)) {
                groups.set(key, {
                    id: key,
                    name,
                    image,
                    hotelCount: 0,
                    minPrice: null,
                });
            }

            const group = groups.get(key);

            group.hotelCount += 1;

            if (!group.image && image) {
                group.image = image;
            }

            if (
                price !== null &&
                (group.minPrice === null || price < group.minPrice)
            ) {
                group.minPrice = price;
            }
        });

        return [...groups.values()]
            .sort(
                (a, b) =>
                    b.hotelCount - a.hotelCount ||
                    a.name.localeCompare(b.name)
            )
            .slice(0, 5);
    }, [hotels]);

    const handleAreaClick = (area) => {
        navigate("/search-hotels", {
            state: {
                destination: "Dubai",
                area: area.name,
                location: area.name,
                checkIn: "",
                checkOut: "",
                guests: 2,
                rooms: 1,
            },
        });

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <section
            id="popular-areas"
            className="bg-white py-12 sm:py-14 lg:py-16"
        >

            <div
                className="
    mx-auto w-full max-w-[1536px]
    px-4 sm:px-6 lg:px-8
  "
            >

                {/* SECTION HEADING */}
                <div
                    className="
            mb-6 flex flex-col justify-between
            gap-4 sm:flex-row sm:items-center
          "
                >
                    <div>
                        {/* SMALL GREEN LABEL */}
                        <p
                            className="
                mb-1 text-[10px] font-extrabold
                uppercase tracking-[0.2em]
                text-[#168B43]
              "
                        >
                            Explore
                        </p>

                        {/* TITLE */}
                        <h2
                            className="
                text-[27px] font-extrabold
                leading-tight tracking-[-0.04em]
                text-[#172D3C]
                sm:text-[32px]
              "
                        >
                            Popular Areas{" "}
                            <span className="text-[#168B43]">
                                in Dubai
                            </span>
                        </h2>

                        {/* DESCRIPTION */}
                        <p
                            className="
                mt-1.5 max-w-[680px]
                text-[12px] leading-5
                text-[#64748B]
                sm:text-[13px]
              "
                        >
                            Stay close to the city's top attractions with
                            our handpicked hotels in the most popular areas.
                        </p>
                    </div>

                    {/* VIEW ALL AREAS */}
                    <button
                        type="button"
                        onClick={() => navigate("/hotels")}
                        className="
              group inline-flex h-9 shrink-0
              items-center justify-center gap-2
              self-start rounded-full
              border border-[#9AB9A8]
              bg-white px-4
              text-[11px] font-bold
              text-[#17324D]
              transition-all duration-200
              hover:border-[#168B43]
              hover:bg-[#E8F7EE]
              sm:self-center
            "
                    >
                        View All Areas
                        <ArrowRight
                            size={14}
                            className="
                transition-transform
                group-hover:translate-x-1
              "
                        />
                    </button>
                </div>

                {/* AREA CARDS */}
                {loading ? (
                    <div
                        className="
              grid grid-cols-2 gap-3
              sm:grid-cols-3 lg:grid-cols-5
            "
                    >
                        {[1, 2, 3, 4, 5].map((item) => (
                            <div
                                key={item}
                                className="
                  h-[255px] animate-pulse
                  rounded-[11px] bg-[#E5F1E9]
                  lg:h-[300px]
                "
                            />
                        ))}
                    </div>
                ) : error ? (
                    <div
                        className="
              rounded-xl border border-[#DCEBE1]
              bg-[#F7FBF8] px-6 py-10 text-center
            "
                    >
                        <ImageOff
                            size={28}
                            className="mx-auto text-[#168B43]"
                        />

                        <p className="mt-3 text-sm text-slate-600">
                            {error}
                        </p>

                        <button
                            type="button"
                            onClick={() => setRetryKey((prev) => prev + 1)}
                            className="
                mt-4 inline-flex items-center gap-2
                rounded-lg bg-[#168B43]
                px-4 py-2.5 text-xs font-bold text-white
              "
                        >
                            <RefreshCw size={14} />
                            Try Again
                        </button>
                    </div>
                ) : areas.length === 0 ? (
                    <div
                        className="
              rounded-xl border border-[#DCEBE1]
              bg-[#F7FBF8] px-6 py-10 text-center
            "
                    >
                        <MapPin
                            size={28}
                            className="mx-auto text-[#168B43]"
                        />

                        <p className="mt-3 text-sm text-slate-600">
                            No hotel areas were found.
                        </p>
                    </div>
                ) : (
                    <div
                        className="
              grid grid-cols-2 gap-3
              sm:grid-cols-3
              lg:grid-cols-5
              lg:gap-3
            "
                    >
                        {areas.map((area) => (
                            <AreaCard
                                key={area.id}
                                area={area}
                                onClick={handleAreaClick}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
